/**
 * Normalise a phone number to E.164 ("+447700900123"). Mirrors optopro-backend
 * `src/common/utils/phone.util.ts`; keep the two in sync. UK-first: national
 * formats ("07700 900123") are read as UK; other countries need "+" or "00".
 * Returns null for numbers SMS providers would reject (e.g. "0347588595").
 */
export function normalizePhoneNumber(raw?: string | null): string | null {
  if (!raw) return null;
  let value = String(raw)
    .trim()
    .replace(/\(0\)/g, '')
    .replace(/[\s\-().]/g, '');
  if (!value) return null;

  if (value.startsWith('00')) value = `+${value.slice(2)}`;

  if (value.startsWith('+')) {
    const digits = value.slice(1);
    if (!/^\d+$/.test(digits)) return null;
    if (digits.startsWith('44')) return normalizeUkNational(digits.slice(2));
    return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  }

  if (!/^\d+$/.test(value)) return null;
  if (value.startsWith('44')) return normalizeUkNational(value.slice(2));
  if (value.startsWith('0')) return normalizeUkNational(value.slice(1));
  return normalizeUkNational(value);
}

function normalizeUkNational(nsn: string): string | null {
  const digits = nsn.replace(/^0/, '');
  // 10 significant digits; 9 only for a few legacy 01xxx landline areas.
  if (!/^(?:[1-9]\d{9}|1\d{8})$/.test(digits)) return null;
  return `+44${digits}`;
}

export function isValidPhoneNumber(raw?: string | null): boolean {
  return normalizePhoneNumber(raw) !== null;
}

export const PHONE_VALIDATION_MESSAGE =
  'Enter a valid UK number (e.g. 07700 900123) or an international number starting with +';

/** Removes characters a phone number can't contain, as the user types. */
export function stripNonPhoneCharacters(value: string): string {
  return value.replace(/[^\d+\s()\-.]/g, '');
}
